/*
 * A message's actions where the pointer cannot hover (VISUAL.md §7.4 and §7.19, X-B2 B2-09).
 *
 * There is no hover on a phone to paint a bar with, so the bar used to become a static pair — the
 * picker and the `⋯` — at every row's top-right. With the 44 px hit area every small control wears
 * on a blunt pointer, two corner buttons cover a quarter of a short row, and a 844 px screen shows
 * three of those pairs over words nobody asked them to stand on. So **no row draws a control**: a
 * press held on the row's body opens everything the row can do as the sheet the details column
 * already uses at that width — three one-tap reactions at its top, then the row menu's own entries
 * in the row menu's order. A tap is still a tap and a link is still a link.
 *
 * The entries are the menu's `MenuItem`s and not a list of this file's own: `MessageRow`'s
 * `menuItems()` is the one place a row decides what it offers and in what order, so the sheet and
 * the `⋯` menu cannot drift apart.
 *
 * **What the reader chose runs after the sheet has left the document and handed the focus back**, not
 * when it was chosen. A dialog stays mounted for the length of its exit animation with its focus trap
 * armed, and gives the focus back to whatever opened it as it goes; an action that opened the editor,
 * a dialog or the picker any earlier than that would be given the focus and have it taken away again
 * — which a popover reads as the focus leaving it and answers by dismissing itself (the defect
 * `ui/Menu` closes with `inert`, which a caller cannot reach from outside the primitive). Waiting for
 * that move puts them in the only order that works: the sheet goes, the row takes the focus back, and
 * then the surface the reader asked for opens and keeps it (`ui/choiceAfterClose.tsx`).
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react"
import { Icon } from "../../../core/shell/index.ts"
import { cx } from "../../../lib/cx.ts"
import { Button, Dialog, useChoiceAfterClose, type MenuItem } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import "../render/chat.css"

/** How long a press is held before it is a press and not a tap. */
const PRESS_MS = 500

/**
 * How far a finger may travel inside that window before the gesture is a scroll instead. A thumb
 * moves a pixel or two while it rests; a flick of the list starts with more than this.
 */
const PRESS_SLOP = 10

/**
 * Whether what happened, happened on the row itself. React's tree is not the document's: the sheet
 * and the emoji picker are portals and remain React children of the row that mounted them, so a press
 * or a click inside either of them arrives at the row's own handlers. Only what a finger did to the
 * row is this gesture's — without this, the first tap inside the sheet was swallowed as the lift of
 * the press that opened it.
 */
function onRow(event: { currentTarget: Element; target: EventTarget | null }): boolean {
	return event.target instanceof Node && event.currentTarget.contains(event.target)
}

/** What a row spreads on its own body to answer the gesture. */
export interface ActionGesture {
	onPointerDown: (event: React.PointerEvent) => void
	onPointerMove: (event: React.PointerEvent) => void
	onPointerUp: () => void
	onPointerCancel: () => void
	onClickCapture: (event: React.MouseEvent) => void
	onContextMenu: (event: React.MouseEvent) => void
}

/**
 * The gesture that asks a row for its actions where nothing on the row can: a press held for half a
 * second, and the platform's own long press (a touch screen reports it as `contextmenu`, and the
 * default is declined because two menus over one message is none — declining it is also what stops
 * the engine turning the press into a text selection). A scroll that starts inside the press window
 * cancels it, whether it arrives as movement or as the `pointercancel` the browser sends when it
 * takes the gesture over for scrolling.
 *
 * `enabled` is the caller's answer to "can this pointer hover": on a pointer that can, the hover bar
 * is the way in and every handler here stands down, so a mouse keeps its own context menu and a
 * press on a word stays a selection.
 */
export function useActionGesture(enabled: boolean, onPress: () => void): ActionGesture {
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const from = useRef<{ x: number; y: number } | undefined>(undefined)
	// True between the press firing and the next gesture: the `click` the finger's lift produces
	// belongs to the press and not to whatever it was resting on.
	const fired = useRef(false)

	const cancel = useCallback(() => {
		if (timer.current !== undefined) clearTimeout(timer.current)
		timer.current = undefined
		from.current = undefined
	}, [])

	// A row that scrolls out of the window while a finger is on it leaves no timer behind.
	useEffect(() => cancel, [cancel])

	// The caller's handler as of the latest commit, so the gesture's identity never changes and a row
	// that re-renders while a finger is on it keeps its timer.
	const latest = useRef(onPress)
	useLayoutEffect(() => {
		latest.current = onPress
	})
	const fire = useCallback(() => {
		cancel()
		fired.current = true
		latest.current()
	}, [cancel])

	return {
		onPointerDown(event) {
			// Any new touch is a new gesture, wherever it lands: whatever click the press was waiting to
			// swallow has had its chance. Where an engine sends no click after a long press at all, this
			// is what stops the reader's next tap paying for it.
			fired.current = false
			if (!enabled || !onRow(event)) return
			// A mouse has the hover bar wherever it is, and a second finger is the start of a pinch.
			if (event.pointerType === "mouse" || !event.isPrimary) {
				cancel()
				return
			}
			from.current = { x: event.clientX, y: event.clientY }
			timer.current = setTimeout(fire, PRESS_MS)
		},
		onPointerMove(event) {
			const start = from.current
			if (start === undefined) return
			const moved =
				Math.abs(event.clientX - start.x) > PRESS_SLOP ||
				Math.abs(event.clientY - start.y) > PRESS_SLOP
			if (moved) cancel()
		},
		onPointerUp: cancel,
		onPointerCancel: cancel,
		onClickCapture(event) {
			// The lift of a press that opened the sheet, swallowed once and wherever it lands. It lands
			// on the sheet as often as on the row — the panel opens under the finger while it is still
			// down — and a lift that chose *Reply in thread* because that is what the sheet drew under
			// the thumb is the gesture answering itself (measured on a 390 × 844 touch context).
			if (!fired.current) return
			fired.current = false
			event.preventDefault()
			event.stopPropagation()
		},
		onContextMenu(event) {
			if (!enabled || !onRow(event)) return
			event.preventDefault()
			if (!fired.current) fire()
		},
	}
}

export interface ActionSheetProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** What the sheet acts on, in words: the author and the time, as the row's own label says them. */
	about: string
	/** The one-tap reactions — the reader's three recent emoji. Empty where this message takes none. */
	quick?: readonly string[] | undefined
	/** Applies one of them. Absent with an empty `quick`. */
	onQuick?: ((emoji: string) => void) | undefined
	/** The row menu's entries, in the row menu's order. */
	items: readonly MenuItem[]
}

export function ActionSheet({
	open,
	onOpenChange,
	about,
	quick = [],
	onQuick,
	items,
}: ActionSheetProps) {
	const { choose, gone } = useChoiceAfterClose(onOpenChange)

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			sheet
			title={t("chat.message.actions")}
			description={about}
		>
			{gone}
			{quick.length === 0 || onQuick === undefined ? null : (
				<div className="tb-msg-sheet__quick" role="group" aria-label={t("chat.actions.quick")}>
					{quick.map((emoji, index) => (
						<Button
							key={`${String(index)}:${emoji}`}
							variant="ghost"
							className="tb-msg-sheet__emoji"
							aria-label={t("chat.msg.reactWith", { emoji })}
							label={emoji}
							onClick={() => choose(() => onQuick(emoji))}
						/>
					))}
				</div>
			)}
			<ul className="tb-msg-sheet__list">
				{items.map((item) => (
					<li
						key={item.id}
						className={cx(
							"tb-msg-sheet__row",
							item.separatorBefore === true && "tb-msg-sheet__row--group",
						)}
					>
						<Button
							variant={item.danger === true ? "danger-quiet" : "ghost"}
							size="lg"
							className="tb-msg-sheet__item"
							label={item.label}
							disabled={item.disabled ?? false}
							{...(item.icon === undefined ? {} : { before: <Icon name={item.icon} size={16} /> })}
							onClick={() => choose(item.onSelect)}
						/>
					</li>
				))}
			</ul>
		</Dialog>
	)
}
